// ============================================================
// useActingAs - the seat says who you act as (v8.363.2).
//
// "Acting as" was a private pointer: nobody else's client could map
// "Brother Cain" to a person, which the vox needs twice - to seed a
// group from a sheet team, and to say "Ana (acting as Brother Cain)" in
// a member list. So the pointer is published on the seat document, the
// way the heartbeat is: a merge write, debounced, '' when acting as
// nobody. The GM sees who acts as whom; it confers nothing.
//
// The seat also REMEMBERS everyone it has acted as (v9.5.3, `actedAs`, a
// stringified list): a called test asks a player for each character they
// have played tonight, not only the one they are set to now. The list is
// kept HERE: the presence listing does not carry a seat's own document
// back to it. In a ref while the page lives, and in the viewer's own
// storage per session (`actedAs:<sessionId>`): the ref starts empty on
// every load, and the first publish after a reload wrote just the current
// character over everything the seat had stored (2026-10-06).
// ============================================================
import { useEffect, useRef } from 'react';
import { diag, errorSummary } from '../diagnostics/log';
import { pushActed } from '../engine/roster/actedAs';
import { getViewerPrefs, setViewerPref } from '../utils/viewerPrefs';

export const ACTING_AS_DEBOUNCE_MS = 300;

interface Params {
  sessionId: string | null;
  playerId: string | null;
  actorId: string | null;
  setPlayerProfile: (sessionId: string, playerId: string, profile: Record<string, unknown>) => Promise<unknown>;
}

/** The seat's profile write: who I act as now, and - when it is someone - everyone so far. */
function publishActing(acted: string[], next: string): Record<string, string> {
  if (!next) return { actingAs: next };
  acted.splice(0, acted.length, ...pushActed(acted, next));
  return { actingAs: next, actedAs: JSON.stringify(acted) };
}

const storeKey = (sessionId: string) => `actedAs:${sessionId}`;

/** Who this viewer acted as in this session before the page was loaded. */
function remembered(sessionId: string | null): string[] {
  const stored: unknown = sessionId ? getViewerPrefs()[storeKey(sessionId)] : null;
  return Array.isArray(stored) ? stored.filter((v): v is string => typeof v === 'string' && v !== '') : [];
}

export default function useActingAs({ sessionId, playerId, actorId, setPlayerProfile }: Params): void {
  const last = useRef<string | null>(null);
  const acted = useRef<string[]>([]);
  // A session starts from what this viewer already did in it, and from nothing published.
  // Declared BEFORE the publish below, so the list is there when the first publish runs.
  useEffect(() => {
    last.current = null;
    acted.current = remembered(sessionId);
  }, [sessionId, playerId]);
  useEffect(() => {
    if (!sessionId || !playerId) return;
    const next = actorId ?? '';
    if (last.current === next) return;
    const t = setTimeout(() => {
      last.current = next;
      const profile = publishActing(acted.current, next);
      setViewerPref(storeKey(sessionId), [...acted.current]);
      setPlayerProfile(sessionId, playerId, profile).catch((e: unknown) =>
        diag.warn('presence', 'could not publish who I act as', errorSummary(e)),
      );
    }, ACTING_AS_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [sessionId, playerId, actorId, setPlayerProfile]);
}
